import React from 'react';
import { Text as MantineText } from '@mantine/core';
import { useIsMobile } from '../../src/context/MediaQueryContext';
import { COLORS } from '../../src/constants/theme';

type TextVariant = 'body' | 'dimmed' | 'small';

/**
 * Design system text tier — encodes the canonical size + color pairing for
 * each information hierarchy level. Established in AttendeeCard / StatCard
 * (#709) and formalized here so callsites stop re-inlining.
 *
 * See CLAUDE.md "Text Tier Map" for the full table.
 *
 * Explicit `size` / `color` props still override the tier for edge cases
 * (e.g. dropdown-density 11px body text).
 */
export type TextTier = 'primary' | 'secondary' | 'tertiary' | 'quaternary';

const TIER_MAP: Record<TextTier, { size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; color: string }> = {
  primary: { size: 'sm', color: COLORS.textPrimary }, // 14px, #f2f2f2 — titles, names
  secondary: { size: 'xs', color: COLORS.textDescription }, // 12px, #d1d1d1 — email, message body
  tertiary: { size: 'xs', color: COLORS.textDimmed }, // 12px, #BBBBBB — supporting metadata
  quaternary: { size: 'xs', color: COLORS.textSubtle }, // 12px, #999999 — dates, order#
};

interface ThemedTextProps {
  children?: React.ReactNode;
  variant?: TextVariant;
  /** Design system tier — sets size + color per the canonical map. Explicit `size`/`color` override. */
  tier?: TextTier;
  className?: string;
  style?: React.CSSProperties;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fw?: 400 | 500 | 600 | 700;
  color?: string;
  // Layout
  mt?: string | number;
  mb?: string | number;
  ml?: string | number;
  mr?: string | number;
  mx?: string | number;
  my?: string | number;
  py?: string | number;
  maw?: string | number;
  // Text styling
  ta?: 'left' | 'center' | 'right' | 'justify';
  tt?: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
  td?: string;
  lh?: string | number;
  fs?: string;
  lineClamp?: number;
  truncate?: boolean | 'start' | 'end';
  inherit?: boolean;
  /**
   * Mantine's own color style-prop. Highest-priority color channel; `color`,
   * `style.color`, `tier` and `variant` all resolve into it (see #1238).
   * Prefer `tier` for design-system text and `color` for one-offs; reach for
   * `c` only when you need a Mantine theme key resolved verbatim.
   */
  c?: string;
  // Rendering
  span?: boolean;
  component?: React.ElementType;
  /**
   * Anchor props, forwarded when `component` is a link (`Link`, `'a'`).
   * ThemedText destructures an explicit list rather than spreading `...rest`,
   * so these have to be named here or they're dropped — the footer shipped
   * seven `href=""` links for four months that way (#1326).
   */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  title?: string;
  'aria-label'?: string;
  'aria-current'?: React.AriaAttributes['aria-current'];
  id?: string;
  dangerouslySetInnerHTML?: { __html: string };
  // Events
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
}

const sizeMap: Record<string, string> = {
  xs: 'clamp(0.75rem, 1.5vw, 0.875rem)',
  sm: 'clamp(0.875rem, 1.75vw, 1rem)',
  md: 'clamp(1rem, 2vw, 1.125rem)',
  lg: 'clamp(1.125rem, 2.25vw, 1.25rem)',
  xl: 'clamp(1.25rem, 2.5vw, 1.5rem)',
};

export const ThemedText: React.FC<ThemedTextProps> = ({
  children,
  variant = 'body',
  tier,
  className = '',
  style = {},
  size,
  fw,
  color,
  mt,
  mb,
  ml,
  mr,
  mx,
  my,
  py,
  maw,
  ta,
  tt,
  td,
  lh,
  fs,
  lineClamp,
  truncate,
  inherit,
  c,
  span,
  component,
  href,
  target,
  rel,
  title,
  'aria-label': ariaLabel,
  'aria-current': ariaCurrent,
  id,
  dangerouslySetInnerHTML,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const isMobile = useIsMobile();

  // Resolve tier tokens; explicit size/color still win.
  const tierTokens = tier ? TIER_MAP[tier] : undefined;
  const resolvedSize = size ?? tierTokens?.size;
  // Priority: c → color → style.color → tier → variant → default. The result
  // is passed as Mantine's `c` style-prop, not just `style.color`, because
  // the global theme sets `Text.defaultProps.c` and Mantine applies
  // style-props after `style` — so `style.color` alone never wins (#1238).
  const styleColor = typeof style.color === 'string' ? style.color : undefined;
  const textColor = c || color || styleColor || tierTokens?.color || (variant === 'dimmed' ? '#dcdcdc' : '#f2f2f2');

  let fontSize: string | undefined;
  if (resolvedSize) {
    fontSize = sizeMap[resolvedSize];
  } else if (variant === 'small') {
    fontSize = isMobile ? '0.75rem' : '0.875rem';
  } else if (variant === 'body') {
    fontSize = isMobile ? '0.875rem' : '1rem';
  }

  return (
    <MantineText
      className={`poppins ${className}`}
      fw={fw}
      mt={mt}
      mb={mb}
      ml={ml}
      mr={mr}
      mx={mx}
      my={my}
      py={py}
      maw={maw}
      ta={ta}
      tt={tt}
      td={td}
      lh={lh}
      fs={fs}
      lineClamp={lineClamp}
      truncate={truncate}
      inherit={inherit}
      c={textColor}
      span={span}
      component={component}
      href={href}
      target={target}
      rel={rel}
      title={title}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      id={id}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        fontFamily: 'Poppins, sans-serif',
        color: textColor,
        fontSize,
        lineHeight: lh || 1.6,
        ...style,
      }}
    >
      {children}
    </MantineText>
  );
};
